import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Focus ring utility — tailwind-design-system pattern.
 * Apply to interactive elements for consistent focus states.
 */
export const focusRing = cn(
  "focus-visible:outline-none focus-visible:ring-2",
  "focus-visible:ring-ring focus-visible:ring-offset-2"
);

/**
 * Disabled utility — tailwind-design-system pattern.
 */
export const disabledStyles = "disabled:pointer-events-none disabled:opacity-50";

/** Chart color palette (CSS variable names) */
export const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
  "var(--chart-7)",
  "var(--chart-8)",
  "var(--chart-9)",
  "var(--chart-10)",
];

/**
 * Get a chart color by index (wraps around).
 */
export function getChartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length];
}

/**
 * Format currency with 2 decimal places (e.g., $1.150,00).
 */
export function formatCurrency(value: number): string {
  return `$${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;
}

/**
 * Format currency without decimal places for reports (e.g., $1.150).
 */
export function formatCurrencyCompact(value: number): string {
  return `$${new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 0,
  }).format(value)}`;
}

/**
 * Format a MySQL DATE value (which may arrive as ISO datetime or YYYY-MM-DD) as dd/MM/yyyy.
 */
export function formatDateBR(value: string | Date | null | undefined): string {
  if (!value) return "";
  const iso = typeof value === "string" ? value : value.toISOString();
  const ymd = iso.slice(0, 10);
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${d}/${m}/${y}`;
}

/**
 * Converte um ISO string (UTC) pro formato "YYYY-MM-DDTHH:mm" que o input `datetime-local`
 * espera no horário local do navegador — `iso.slice(0, 16)` direto pega o horário em UTC.
 */
export function toLocalDatetimeInputValue(iso: string): string {
  const d = new Date(iso);
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
}

const WEEKDAY_ABBR = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];

export function weekdayAbbrBR(value: string | Date | null | undefined): string {
  if (!value) return "";
  const iso = typeof value === "string" ? value : value.toISOString();
  const ymd = iso.slice(0, 10);
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return "";
  const dt = new Date(Date.UTC(y, m - 1, d));
  return WEEKDAY_ABBR[dt.getUTCDay()] ?? "";
}

/**
 * Format number with locale.
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value);
}

/**
 * Capitalize each word while respecting Portuguese accents.
 */
export function toTitleCase(value: string): string {
  return value
    .toLocaleLowerCase("pt-BR")
    .replace(/(?:^|\s)([a-zá-ú])/gi, (match) => match.toLocaleUpperCase("pt-BR"));
}
